import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play, RotateCcw } from "lucide-react";

const N = 4;
const SIZE = N * N;
const solved = () => [...Array(SIZE - 1).keys()].map((i) => i + 1).concat(0);

function shuffle() {
  let arr = solved();
  // do random valid moves to guarantee solvable
  let blank = SIZE - 1;
  for (let i = 0; i < 300; i++) {
    const r = Math.floor(blank / N), c = blank % N;
    const moves = [];
    if (r > 0) moves.push(blank - N);
    if (r < N - 1) moves.push(blank + N);
    if (c > 0) moves.push(blank - 1);
    if (c < N - 1) moves.push(blank + 1);
    const m = moves[Math.floor(Math.random() * moves.length)];
    [arr[blank], arr[m]] = [arr[m], arr[blank]];
    blank = m;
  }
  return arr;
}

const isSolved = (arr) => arr.every((v, i) => (i === SIZE - 1 ? v === 0 : v === i + 1));

export function SlidingPuzzle({ onScore }) {
  const [tiles, setTiles] = useState(solved);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [running, setRunning] = useState(false);
  const [won, setWon] = useState(false);
  const reported = useRef(false);

  useEffect(() => {
    if (!running || won) return;
    const id = setInterval(() => setTime((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running, won]);

  const start = () => {
    setTiles(shuffle()); setMoves(0); setTime(0); setRunning(true); setWon(false); reported.current = false;
  };

  const move = (i) => {
    if (!running || won) return;
    const blank = tiles.indexOf(0);
    const r = Math.floor(i / N), c = i % N;
    const br = Math.floor(blank / N), bc = blank % N;
    if (Math.abs(r - br) + Math.abs(c - bc) !== 1) return;
    const nt = [...tiles];
    [nt[i], nt[blank]] = [nt[blank], nt[i]];
    setTiles(nt);
    setMoves((m) => m + 1);
    if (isSolved(nt)) { setWon(true); setRunning(false); }
  };

  useEffect(() => {
    if (won && !reported.current) {
      reported.current = true;
      onScore?.(Math.max(100, 2000 - moves * 10 - time * 5));
    }
  }, [won, moves, time, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="sliding-puzzle">
      <div className="flex items-center gap-6 mb-4 font-mono text-sm">
        <span className="text-neon-cyan">MOVES: {moves}</span>
        <span className="text-neon-yellow">TIME: {time}s</span>
        {running && <Button onClick={start} size="sm" variant="outline" className="border-zinc-700 text-white rounded-none h-8"><RotateCcw className="w-3 h-3 mr-1" />Shuffle</Button>}
      </div>

      <div className="relative grid gap-2 p-2 bg-zinc-900 rounded-xl border-2 border-zinc-700"
        style={{ width: "min(88vw, 340px)", gridTemplateColumns: `repeat(${N}, 1fr)` }}>
        {tiles.map((v, i) => (
          <button key={i} onClick={() => move(i)} data-testid={`slide-tile-${v}`}
            className="aspect-square rounded-lg font-display font-black text-2xl flex items-center justify-center transition-all"
            style={{
              background: v === 0 ? "transparent" : "#18181b",
              border: v === 0 ? "2px dashed #27272a" : "2px solid #00F0FF44",
              color: "#00F0FF", cursor: running && !won ? "pointer" : "default",
            }}>
            {v === 0 ? "" : v}
          </button>
        ))}

        {(!running || won) && (
          <div className="absolute inset-0 bg-black/85 rounded-xl flex flex-col items-center justify-center gap-3">
            {won && <p className="font-display font-black text-2xl text-neon-lime uppercase" data-testid="slide-won">Solved! {moves} moves · {time}s</p>}
            <Button onClick={start} data-testid="slide-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {won ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Slide tiles to order them 1–15. Lowest moves = highest score.</p>
    </div>
  );
}
